import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom-v5-compat';

import { AppEvents } from '@grafana/data';
import { t, Trans } from '@grafana/i18n';
import { getAppEvents, locationService } from '@grafana/runtime';
import { Alert, Box, EmptyState, Grid } from '@grafana/ui';
import { CustomDashboardTemplateInteractions } from 'app/features/dashboard-scene/analytics/dashboard-templates/main';

import { AnnoKeyCreatedBy } from '../../features/apiserver/types';
import { DashboardCard } from '../../features/dashboard/dashgrid/DashboardLibrary/DashboardCard';
import { TemplateFilters } from '../../features/dashboard/dashgrid/DashboardLibrary/TemplateFilters';
import { NewDashboardLibraryInteractions } from '../../features/dashboard/dashgrid/DashboardLibrary/analytics/main';
import {
  CONTENT_KINDS,
  CREATION_ORIGINS,
  DashboardTemplatesSourceEntryPointMap,
  DISCOVERY_METHODS,
  EVENT_LOCATIONS,
} from '../../features/dashboard/dashgrid/DashboardLibrary/constants';
import { type DashboardTemplatesTabProps } from '../../features/dashboard/dashgrid/DashboardLibrary/enterprise-components/DashboardTemplatesTabExtension';
import { canManageDashboardTemplates } from '../../features/dashboard/dashgrid/DashboardLibrary/utils/templatePermissions';
import { DASHBOARD_LIBRARY_ROUTES } from '../../features/dashboard/dashgrid/types';
import { type DashboardTemplate, useDeleteDashboardTemplateMutation } from '../api/clients/dashboardtemplates/v1alpha1';

import { isSafeDashboardPreviewImage } from './previewImage';
import { useAllDashboardTemplates } from './useAllDashboardTemplates';
import { useTemplateFiltering } from './useTemplateFiltering';

function getTemplateTitle(template: DashboardTemplate): string {
  return template.spec.title ?? '';
}

function getTemplateDescription(template: DashboardTemplate): string {
  return template.spec.description ?? '';
}

function getTemplateTags(template: DashboardTemplate): string[] {
  return template.spec.tags ?? [];
}

export function DashboardTemplatesTab({ isOpen, onClose, loadedFiredRef }: DashboardTemplatesTabProps) {
  const [searchParams] = useSearchParams();
  const entryPoint = DashboardTemplatesSourceEntryPointMap[searchParams.get('source') ?? ''] || 'unknown';

  const canManageTemplates = canManageDashboardTemplates();

  const {
    items: dashboardTemplates,
    isLoading: dashboardTemplatesLoading,
    error,
    refetch,
  } = useAllDashboardTemplates({ skip: !isOpen });

  const [deleteTemplate] = useDeleteDashboardTemplateMutation();

  const {
    searchQuery,
    setSearchQuery,
    selectedTags,
    onTagsChange,
    selectedCreators,
    onCreatorsChange,
    sortValue,
    onSortChange,
    filteredItems,
    getTagOptions,
    creatorOptions,
    getSortOptions,
  } = useTemplateFiltering({
    items: dashboardTemplates,
    getTitle: getTemplateTitle,
    getDescription: getTemplateDescription,
    getTags: getTemplateTags,
    getCreatorUid: (t) => t.metadata?.annotations?.[AnnoKeyCreatedBy] ?? '',
    entryPoint,
  });

  const getCreatorDisplayName = (uid: string): string | undefined => {
    if (!uid) {
      return undefined;
    }
    return creatorOptions.find((opt) => opt.value === uid)?.label ?? uid;
  };

  const trackItemClicked = (template: DashboardTemplate, action: 'view_template' | 'edit_template' | 'assistant') => {
    NewDashboardLibraryInteractions.itemClicked({
      contentKind: CONTENT_KINDS.CUSTOM_DASHBOARD_TEMPLATE,
      datasourceTypes: [],
      libraryItemId: template.metadata.name ?? '',
      libraryItemTitle: getTemplateTitle(template),
      sourceEntryPoint: entryPoint,
      eventLocation: EVENT_LOCATIONS.BROWSE_DASHBOARDS_PAGE,
      discoveryMethod: searchQuery ? DISCOVERY_METHODS.SEARCH : DISCOVERY_METHODS.BROWSE,
      action,
    });
  };

  const onUseDashboardTemplate = (template: DashboardTemplate, customizeWithAssistant?: boolean) => {
    trackItemClicked(template, customizeWithAssistant ? 'assistant' : 'view_template');
    onClose();
    const params = new URLSearchParams({
      dashboardTemplateUid: template.metadata.name ?? '',
      useTemplateBanner: 'true',
      creationOrigin: CREATION_ORIGINS.DASHBOARD_LIBRARY_CUSTOM_DASHBOARD_TEMPLATE,
      sourceEntryPoint: entryPoint,
      libraryItemId: template.metadata.name ?? '',
    });
    locationService.push(`${DASHBOARD_LIBRARY_ROUTES.Template}?${params.toString()}`);
  };

  const onEditDashboardTemplate = (template: DashboardTemplate) => {
    trackItemClicked(template, 'edit_template');
    onClose();
    const params = new URLSearchParams({
      dashboardTemplateUid: template.metadata.name ?? '',
      editTemplate: 'true',
      sourceEntryPoint: entryPoint,
    });
    locationService.push(`${DASHBOARD_LIBRARY_ROUTES.Template}?${params.toString()}`);
  };

  const onDeleteDashboardTemplate = async (template: DashboardTemplate) => {
    const title = getTemplateTitle(template);
    try {
      await deleteTemplate({ name: template.metadata.name ?? '' }).unwrap();
      CustomDashboardTemplateInteractions.deleted({
        templateUid: template.metadata.name ?? '',
        eventLocation: EVENT_LOCATIONS.DASHBOARD_TEMPLATES_MODAL,
      });
      getAppEvents().publish({
        type: AppEvents.alertSuccess.name,
        payload: [
          t('dashboard-library.custom-dashboard-template-modal.delete-success', 'Deleted template "{{title}}"', {
            title,
          }),
        ],
      });
      refetch();
    } catch (e) {
      getAppEvents().publish({
        type: AppEvents.alertError.name,
        payload: [
          t('dashboard-library.custom-dashboard-template-modal.delete-error', 'Failed to delete template "{{title}}"', {
            title,
          }),
        ],
      });
      throw e;
    }
  };

  // Fire `loaded` once per open, after templates resolve. Mounting this component means
  // the custom templates tab is the selected view, so this tracks tab selection. The
  // parent-owned ref guarantees it does not re-fire when the user toggles back to this tab.
  useEffect(() => {
    if (loadedFiredRef.current || dashboardTemplatesLoading) {
      return;
    }
    loadedFiredRef.current = true;
    NewDashboardLibraryInteractions.loaded({
      numberOfItems: dashboardTemplates.length,
      contentKinds: [CONTENT_KINDS.CUSTOM_DASHBOARD_TEMPLATE],
      datasourceTypes: [],
      sourceEntryPoint: entryPoint,
      eventLocation: EVENT_LOCATIONS.BROWSE_DASHBOARDS_PAGE,
    });
  }, [dashboardTemplatesLoading, dashboardTemplates.length, entryPoint, loadedFiredRef]);

  return (
    <>
      {!dashboardTemplatesLoading && error && (
        <Alert
          title={t(
            'dashboard-library.custom-dashboard-template-modal.error-title',
            'Error loading dashboard templates'
          )}
          severity="error"
        >
          <Trans i18nKey="dashboard-library.custom-dashboard-template-modal.error-description">
            Failed to load dashboard templates. Please try again.
          </Trans>
        </Alert>
      )}
      {!dashboardTemplatesLoading && !error && dashboardTemplates.length === 0 && (
        <EmptyState
          variant="call-to-action"
          message={t(
            'dashboard-library.custom-dashboard-template-modal.no-dashboard-templates-title',
            'No template dashboards have been created'
          )}
        >
          <Trans i18nKey="dashboard-library.custom-dashboard-template-modal.no-dashboard-templates-description">
            To get started, save a dashboard as a template{' '}
          </Trans>
        </EmptyState>
      )}
      {!dashboardTemplatesLoading && dashboardTemplates.length > 0 && (
        <Box marginTop={0.5}>
          <TemplateFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            tags={selectedTags}
            onTagsChange={onTagsChange}
            getTagOptions={getTagOptions}
            creatorOptions={creatorOptions}
            selectedCreators={selectedCreators}
            onCreatorsChange={onCreatorsChange}
            sortValue={sortValue}
            onSortChange={onSortChange}
            getSortOptions={getSortOptions}
          />
        </Box>
      )}
      {!dashboardTemplatesLoading && dashboardTemplates.length > 0 && filteredItems.length === 0 && (
        <EmptyState
          variant="not-found"
          message={t(
            'dashboard-library.custom-dashboard-template-filters.no-results-found',
            'No template dashboards found'
          )}
        />
      )}
      <Grid
        gap={4}
        columns={{
          xs: 1,
          sm: 2,
          lg: 3,
        }}
      >
        {dashboardTemplatesLoading
          ? Array.from({ length: 4 }).map((_, index) => <DashboardCard.Skeleton key={index} />)
          : filteredItems.map((template: DashboardTemplate, index: number) => {
              const title = getTemplateTitle(template);
              const description = getTemplateDescription(template);
              const tags = getTemplateTags(template);
              const creatorUid = template.metadata?.annotations?.[AnnoKeyCreatedBy] ?? '';
              const createdByName = creatorUid ? getCreatorDisplayName(creatorUid) : undefined;
              // Captured client-side at save/update time. Falls back to the text card
              // when empty (capture unavailable or failed in the browser).
              const previewImage = isSafeDashboardPreviewImage(template.spec.previewImage)
                ? template.spec.previewImage
                : undefined;

              const dashboardCompat = {
                id: template.metadata.name ?? '',
                name: title,
                description,
              };

              return (
                <DashboardCard
                  key={template.metadata.name}
                  title={title}
                  imageUrl={previewImage}
                  onClick={(customizeWithAssistant?: boolean) =>
                    onUseDashboardTemplate(template, customizeWithAssistant)
                  }
                  onClose={onClose}
                  dashboard={dashboardCompat}
                  kind="custom_dashboard_template"
                  showAssistantButton={false}
                  onEdit={canManageTemplates ? () => onEditDashboardTemplate(template) : undefined}
                  onDelete={canManageTemplates ? () => onDeleteDashboardTemplate(template) : undefined}
                  tags={tags}
                  createdByName={createdByName}
                />
              );
            })}
      </Grid>
    </>
  );
}
